import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React, { ChangeEvent, useState, type JSX } from 'react';
import { UnterschriftenCheckboxState } from '../screens/Vorgang/Unterschriften';
import '../screens/Vorgang/Unterschriften.css';
import { Vorgang } from '../types';
import { SPACING_BETWEEN_FORM_FIELDS } from './common/spacings';
import { Modal } from './Modal';
import { formatDDMMYYYY } from '../shared/dateTime';
import { Signature } from './common/Signature';
import { Gutachtenart, Unterzeichner } from '../shared/constants';
import { useDateiUrl } from '../hooks/useDateiUrl';
import { anredeAuswahl } from '../frontendConstants';

type Props = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly updateCheckboxState: (newState: Partial<UnterschriftenCheckboxState>) => void;
  readonly checkboxState: UnterschriftenCheckboxState;
};

export function Zahlungsanweisung({ vorgang, isLoading, updateCheckboxState, checkboxState }: Props): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Grid size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checkboxState.zahlungsanweisung}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateCheckboxState({ zahlungsanweisung: event.target.checked })}
              name="zahlungsanweisung"
              color="primary"
              data-testid="zahlungsanweisungCheckbox"
            />
          }
          label={
            <>
              Ich habe die AGB{' '}
              <Link
                onClick={(event) => {
                  event.preventDefault();
                  setIsModalOpen(true);
                }}
                data-testid="zahlungsanweisungLink"
              >
                Zahlungsanweisung
              </Link>{' '}
              zur Kenntnis genommen und bin mit der Geltung einverstanden.*
            </>
          }
        />
      </Grid>
      {isModalOpen && <ZahlungsanweisungModal vorgang={vorgang} isLoading={isLoading} isModalOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />}
    </>
  );
}

type ModalProps = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly isModalOpen: boolean;
  readonly onClose: () => void;
};

function ZahlungsanweisungModal({ vorgang, isLoading, isModalOpen, onClose }: ModalProps): JSX.Element {
  const zahlungsanweisungUrl = useDateiUrl(vorgang?.unterschriften?.zahlungsanweisung?.key);

  const signDate = vorgang?.unterschriften?.zahlungsanweisung?.datum ?? new Date().toISOString();

  return (
    <Modal
      openModal={isModalOpen}
      setOpenModal={onClose}
      title="Zahlungsanweisung"
      body={
        <Grid container spacing={SPACING_BETWEEN_FORM_FIELDS} data-testid="zahlungsanweisungModal">
          <Grid size={12}>
            <Typography variant="body2">Ich,</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2" data-testid="auftraggeber">
              {anredeAuswahl.find((anrede) => anrede.value === vorgang.auftraggeber?.anrede)?.label} {vorgang.auftraggeber?.vorname}{' '}
              {vorgang.auftraggeber?.nachname}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              weise den Schadensersatzschuldner (Fahrer / Halter / Haftpflichtversicherer des am Unfall beteiligten, gegnerischen Fahrzeugs) an, die Kosten für
              die {vorgang.gutachtenart === Gutachtenart.REPARATURNACHWEIS ? 'Reparaturbestätigung' : 'Gutachtenerstellung'} gem. Rechnung des Sachverständigen
              aus Anlass des Schadensereignisses vom
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2" data-testid="unfalldatum">
              {formatDDMMYYYY(vorgang.unfalldaten.datum)}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">aus der fälligen Gesamtentschädigungsleistung ohne Abzug direkt an</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              Experts24 Tassone GmbH
              <br />
              Stadt-Sparkasse Solingen
              <br />
              IBAN DE20 3425 0000 0001 6439 15
              <br />
              BIC SOLSDE33XXX
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">zu zahlen.</Typography>
          </Grid>
          <Grid size={12}>
            <Signature
              onSign={() => {}}
              onDeleteSignature={() => {}}
              signatureUrl={zahlungsanweisungUrl}
              isLoading={isLoading}
              unterzeichnerAuswahl={vorgang?.unterschriften?.zahlungsanweisung?.unterzeichner ?? Unterzeichner.AUFTRAGGEBER}
              vorgang={vorgang}
              date={signDate}
              readOnly={true}
            />
          </Grid>
        </Grid>
      }
      footer={
        <div className="unterschriften__modal-button">
          <Button color="primary" variant="contained" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    />
  );
}
